import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { upsertLibraryLesson } from '../assets/data';

const cefrOptions = ['A1','A2','B1','B2','C1','C2'];
const mediumOptions = ['text','audio','video'];
const skillsOptions = ['Speaking','Listening','Reading','Writing'];

export default function PublishModal({ open, onClose, lessonDraft, teacherId = 'teacher-1' }) {
  const initialCover = useMemo(() => {
    const docBanners = lessonDraft && typeof lessonDraft.docBanners === 'object' && lessonDraft.docBanners !== null ? Object.values(lessonDraft.docBanners).filter(Boolean) : [];
    return lessonDraft?.cover || lessonDraft?.bannerImage || (docBanners.length ? docBanners[0] : '');
  }, [lessonDraft]);

  const [title, setTitle] = useState(lessonDraft?.title || '');
  const [description, setDescription] = useState(lessonDraft?.description || '');
  const [cover, setCover] = useState(initialCover || '');
  const [cefr, setCefr] = useState([]);
  const [topics, setTopics] = useState([]);
  const [medium, setMedium] = useState('text');
  const [skills, setSkills] = useState([]);
  const [durationMin, setDurationMin] = useState(45);
  const [priceType, setPriceType] = useState('free');
  const [amount, setAmount] = useState(0);
  const [currency, setCurrency] = useState('USD');
  const [errorMsg, setErrorMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const canSubmit = Boolean(title?.trim() && description?.trim() && cover);

  const toggle = (arr, setter, value) => {
    setter(arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]);
  };

  const handleSubmit = () => {
    if (submitting) return;
    if (!title?.trim() || !description?.trim() || !cover) {
      setErrorMsg('Please fill Title, Description and Cover.');
      return;
    }
    setErrorMsg('');
    setSubmitting(true);
    const now = new Date().toISOString();
    const payload = {
      id: lessonDraft?.id?.startsWith('pub-') ? lessonDraft.id : `pub-${Date.now()}`,
      title: title.trim(),
      description: description.trim(),
      cover,
      topics,
      cefrLevels: cefr,
      medium,
      skills,
      durationMin,
      price: priceType === 'free' ? { type: 'free' } : { type: 'paid', amount: Number(amount) || 0, currency },
      visibility: 'public',
      isTemplate: true,
      authorId: String(teacherId),
      status: 'published',
      moderation: 'approved',
      approved: true,
      createdAt: lessonDraft?.createdAt || now,
      updatedAt: now,
    };
    upsertLibraryLesson(payload);
    if (onClose) onClose(payload);
  };

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') { onClose && onClose(null); } };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div onClick={() => onClose && onClose(null)} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 880, maxWidth: '95vw', background: '#fff', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden', transform: 'translateZ(0)' }}>
        <div style={{ padding: 16, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>Publish lesson</h3>
          <button onClick={() => onClose && onClose(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>

        {errorMsg ? (
          <div style={{ padding: '12px 16px', color: '#b91c1c', background: '#fee2e2', borderTop: '1px solid #fecaca', borderBottom: '1px solid #fecaca' }}>
            {errorMsg}
          </div>
        ) : null}
        <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label>Heading</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Lesson heading" style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }} />
          </div>
          <div>
            <label>Cover image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files && e.target.files[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = (ev) => {
                  const result = ev.target && ev.target.result ? String(ev.target.result) : '';
                  if (result) setCover(result);
                };
                reader.readAsDataURL(file);
              }}
              style={{ display: 'block', width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
            />
            {cover ? (
              <div style={{ marginTop: 8, border: '1px solid #eee', borderRadius: 8, padding: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                <img src={cover} alt="Cover preview" style={{ display: 'block', maxHeight: 80, borderRadius: 6 }} />
                <button className="chip" onClick={() => setCover('')}>Remove</button>
              </div>
            ) : null}
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="Short description (80-200 chars)" style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', resize: 'vertical' }} />
          </div>
          <div>
            <label>CEFR Level</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {cefrOptions.map(lvl => (
                <button key={lvl} onClick={() => toggle(cefr, setCefr, lvl)} className={cefr.includes(lvl) ? 'chip active' : 'chip'}>{lvl}</button>
              ))}
            </div>
          </div>
          <div>
            <label>Medium</label>
            <select value={medium} onChange={e => setMedium(e.target.value)} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}>
              {mediumOptions.map(m => (<option key={m} value={m}>{m}</option>))}
            </select>
          </div>
          <div>
            <label>Skills</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {skillsOptions.map(s => (
                <button key={s} onClick={() => toggle(skills, setSkills, s)} className={skills.includes(s) ? 'chip active' : 'chip'}>{s}</button>
              ))}
            </div>
          </div>
          <div>
            <label>Duration (min)</label>
            <input type="number" min={5} max={180} value={durationMin} onChange={e => setDurationMin(Number(e.target.value) || 0)} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }} />
          </div>
          <div>
            <label>Price</label>
            <select value={priceType} onChange={e => setPriceType(e.target.value)} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}>
              <option value="free">Free</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          {priceType === 'paid' && (
            <>
              <div>
                <label>Amount</label>
                <input type="number" min={0} step={0.01} value={amount} onChange={e => setAmount(e.target.value)} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }} />
              </div>
              <div>
                <label>Currency</label>
                <select value={currency} onChange={e => setCurrency(e.target.value)} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}>
                  {['USD','EUR','RUB','AMD'].map(c => (<option key={c} value={c}>{c}</option>))}
                </select>
              </div>
            </>
          )}
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Topics (comma separated)</label>
            <input value={topics.join(', ')} onChange={e => setTopics(e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="e.g. Business English, Negotiations" style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }} />
          </div>
        </div>

        <div style={{ padding: 16, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={() => onClose && onClose(null)} className="chip">Cancel</button>
          <button onClick={handleSubmit} className="chip" disabled={submitting} style={{ background: 'linear-gradient(135deg, #38b6ff, #00bf63)', color: '#fff', opacity: submitting ? 0.7 : 1 }}>
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}


